import React, { useMemo, useState } from 'react';
import { searchColleges, useColleges } from '../utils/colleges';
import { MIN_SCHOOL_QUERY_CHARS, MAX_SCHOOL_RESULTS } from '../hooks/useSchoolData';
import Combobox from './Combobox';

const NO_COLLEGES = [];

/**
 * College autocomplete on the shared Combobox: the same minimum characters,
 * scrolling list and "keep typing" hint as SchoolPicker, with the ranked
 * `searchColleges` matcher ("kansas state" finds Kansas State, "miami of ohio"
 * finds Miami University).
 *
 * The caller owns the text (`value`) and decides what a pick and free text mean:
 *   onInputChange(text)   every keystroke; nothing is committed
 *   onSelect(college)     an explicit pick: {u, n, c, s, d, l?}
 *   onCommitText(text)    optional; free text, once, when the field is left
 *                         (omit it if the box should only ever hold a pick)
 */
export default function CollegePicker({
  value,
  onInputChange,
  onSelect,
  onCommitText,
  collegesData, // optional: pass rows to skip the shared loader
  placeholder = 'Start typing college name...',
  className = 'form-field__input',
  inputId,
  ariaLabel = 'College',
  disabled,
  classNames: classOverrides = {},   // restyle without forking: root, input, popup, option, optionActive, footer
  rootStyle,
  autoFocus,
  inputProps: extraInputProps,
  renderItem: renderOverride,         // optional (college) => node
  children,
}) {
  const loaded = useColleges();      // null until /data/colleges.json lands
  const list = collegesData || loaded || NO_COLLEGES;
  // Suggestions only follow typing, so a saved value that merely loads into the
  // box does not pop a list open on focus.
  const [typing, setTyping] = useState(false);

  const hits = useMemo(() => (
    typing && (value || '').trim().length >= MIN_SCHOOL_QUERY_CHARS
      ? searchColleges(list, value, MAX_SCHOOL_RESULTS + 1)
      : []
  ), [typing, value, list]);

  const items = hits.slice(0, MAX_SCHOOL_RESULTS);
  const hasMore = hits.length > MAX_SCHOOL_RESULTS;

  return (
    <Combobox
      value={value || ''}
      onInputChange={(text) => { setTyping(true); onInputChange(text); }}
      items={items}
      getKey={(c) => `${c.u || c.n}-${c.c}-${c.s}`}
      renderItem={renderOverride || ((c) => (
        <>
          <div className="school-picker__name">{c.n}</div>
          <div className="school-picker__subtitle">{[c.c, c.s].filter(Boolean).join(', ')}</div>
        </>
      ))}
      onSelect={(c) => { setTyping(false); onSelect(c); }}
      onCommitText={onCommitText ? (text) => { setTyping(false); onCommitText(text); } : undefined}
      footer={hasMore
        ? `Showing the first ${MAX_SCHOOL_RESULTS} matches — keep typing (name, city or state) to narrow`
        : null}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      disabled={disabled}
      rootStyle={rootStyle}
      classNames={{
        root: 'school-picker',
        input: className,
        popup: 'school-picker__results',
        option: 'school-picker__result',
        optionActive: 'school-picker__result--active',
        footer: 'school-picker__hint',
        ...classOverrides,
      }}
      autoFocus={autoFocus}
      inputProps={{ ...(inputId ? { id: inputId } : {}), ...extraInputProps }}
    >
      {children}
    </Combobox>
  );
}
